"use client";

import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { HeartIcon } from "@/components/editorial-system/editorial-primitives";
import { EmojiReactions } from "@/components/editorial-system/reactions";
import { toggleEditorialPostReactionAction } from "@/lib/actions/editorials";
import type { ReactionSummary } from "@/types/database";

/** Under a story: likes and replies, in Meta. */
export function StorySocial({
	slug,
	initialHearts,
	initialHasHeart,
	replies,
	canReact,
	isAuthenticated,
	reactions = [],
	userReactions = [],
}: {
	slug: string;
	initialHearts: number;
	initialHasHeart: boolean;
	replies: number;
	canReact: boolean;
	isAuthenticated: boolean;
	/** Emoji reactions (the heart is counted separately). */
	reactions?: ReactionSummary[];
	userReactions?: string[];
}) {
	const router = useRouter();
	const [hearts, setHearts] = useState(initialHearts);
	const [hasHeart, setHasHeart] = useState(initialHasHeart);
	const [isPending, startTransition] = useTransition();

	function like() {
		if (!canReact) {
			router.push(
				isAuthenticated
					? "/apply"
					: `/login?redirectedFrom=${encodeURIComponent(`/editorial/${slug}`)}`,
			);
			return;
		}
		const next = !hasHeart;
		setHasHeart(next);
		setHearts((count) => (next ? count + 1 : Math.max(0, count - 1)));
		startTransition(async () => {
			const result = await toggleEditorialPostReactionAction(slug, "heart");
			if (!result.success) {
				setHasHeart(!next);
				setHearts((count) => (next ? Math.max(0, count - 1) : count + 1));
			}
		});
	}

	return (
		<div className="ed-meta ed-row" style={{ gap: 12 }}>
			<button
				type="button"
				className="ed-button-text ed-row"
				style={{ gap: 4 }}
				onClick={like}
				aria-pressed={hasHeart}
				disabled={isPending}
			>
				<HeartIcon />
				{hearts}
				<span className="sr-only">{hasHeart ? " Unlike" : " Like"}</span>
			</button>
			<a href="#replies">
				{replies} {replies === 1 ? "reply" : "replies"}
			</a>
			<EmojiReactions
				label="story"
				summary={reactions.filter((item) => item.reaction_type !== "heart")}
				mine={userReactions.filter((item) => item !== "heart")}
				signedIn={canReact}
				onToggle={(emoji) => toggleEditorialPostReactionAction(slug, emoji)}
			/>
		</div>
	);
}
